import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Building2, Send, Mail, FileText, Shield, Target, Wallet, Activity,
  CheckCircle2, AlertTriangle, Hourglass, ArrowRight, X, Download, Eye,
  Sparkles, Calendar, MapPin, DollarSign, TrendingUp, Award, Lock, Banknote,
} from 'lucide-react';

/**
 * SHARE WITH BANK — visual mockup preview.
 * Public route /share-with-bank-preview, no auth.
 * Shows the proposed "Share with Bank" one-click flow:
 *   1) "Share with Bank" button on Command Center
 *   2) Email composition dialog (loan officer, message)
 *   3) Generated PDF preview (Portfolio Summary + Bank-Ready breakdown + Compliance)
 */

const fmt$ = (n) => '$' + (n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

// ---- Component: Step 1 - Command Center entry point ----
const Step1Entry = ({ onShare }) => (
  <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200">
    <div className="text-xs text-slate-500 mb-2 uppercase tracking-wide font-semibold">Step 1 · Click "Share with Bank"</div>
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
            Command Center <Badge className="bg-amber-200 text-amber-900 hover:bg-amber-200">NEW</Badge>
          </h3>
          <p className="text-xs text-slate-500">Your portfolio at a glance</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs"><DollarSign className="w-3 h-3 mr-1" />Request Draw</Button>
          <Button
            size="sm"
            className="h-8 text-xs bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 text-white shadow-md"
            onClick={onShare}
            data-testid="share-with-bank-trigger"
          >
            <Send className="w-3 h-3 mr-1" />Share with Bank
            <Sparkles className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2">
          <Wallet className="w-3.5 h-3.5 text-emerald-700 mb-1" />
          <p className="text-[9px] text-emerald-700 font-bold uppercase">Available</p>
          <p className="text-base font-bold text-slate-900 font-mono">$730K</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
          <Activity className="w-3.5 h-3.5 text-blue-700 mb-1" />
          <p className="text-[9px] text-blue-700 font-bold uppercase">Burn (MTD)</p>
          <p className="text-base font-bold text-slate-900 font-mono">$4.4K</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-2">
          <Target className="w-3.5 h-3.5 text-purple-700 mb-1" />
          <p className="text-[9px] text-purple-700 font-bold uppercase">Bank-Ready</p>
          <p className="text-base font-bold text-slate-900 font-mono">82/100</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
          <Shield className="w-3.5 h-3.5 text-amber-700 mb-1" />
          <p className="text-[9px] text-amber-700 font-bold uppercase">Compliance</p>
          <p className="text-base font-bold text-slate-900 font-mono">7/12</p>
        </div>
      </div>
    </div>
  </div>
);

// ---- Component: Step 2 - Compose email dialog ----
const Step2Compose = ({ onSend, onCancel }) => (
  <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200">
    <div className="text-xs text-slate-500 mb-2 uppercase tracking-wide font-semibold">Step 2 · Send to Your Loan Officer</div>
    <div className="bg-white rounded-2xl border border-slate-200 shadow-xl max-w-md mx-auto">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center text-white">
            <Send className="w-4 h-4" />
          </div>
          <h3 className="font-bold text-slate-900">Share with Bank</h3>
        </div>
        <button onClick={onCancel} className="w-7 h-7 rounded-md hover:bg-slate-100 flex items-center justify-center">
          <X className="w-4 h-4 text-slate-500" />
        </button>
      </div>
      <div className="px-5 py-4 space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Bank / Loan Officer Name</Label>
          <Input placeholder="e.g., Pearson Realty Bank" defaultValue="Hills Bank" className="h-9 text-sm" data-testid="share-bank-name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Loan Officer Email</Label>
          <Input placeholder="loanofficer@yourbank.com" defaultValue="m.sanders@hillsbank.com" type="email" className="h-9 text-sm" data-testid="share-bank-email" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Message (optional)</Label>
          <textarea
            className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 min-h-[70px] focus:outline-none focus:ring-2 focus:ring-blue-500"
            defaultValue="Hi Mike, here's a portfolio update for our active loans. Bank-Ready score is at 82/100 with full compliance on 7 of 12 active contractors. Happy to walk through any questions."
            data-testid="share-bank-message"
          />
        </div>

        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-2">Attachments (auto-generated)</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs">
              <FileText className="w-3.5 h-3.5 text-rose-600" />
              <span className="font-medium text-slate-900 flex-1">DrawReady_Portfolio_Snapshot.pdf</span>
              <span className="text-[10px] text-slate-500">147 KB</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <FileText className="w-3.5 h-3.5 text-emerald-600" />
              <span className="font-medium text-slate-900 flex-1">Compliance_Status.pdf</span>
              <span className="text-[10px] text-slate-500">63 KB</span>
            </div>
          </div>
        </div>

        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input type="checkbox" defaultChecked className="rounded" />
          CC me on this email
        </label>
      </div>
      <div className="px-5 py-3 border-t border-slate-100 flex gap-2 justify-end">
        <Button size="sm" variant="ghost" onClick={onCancel} className="text-xs h-8">Cancel</Button>
        <Button
          size="sm"
          className="text-xs h-8 bg-gradient-to-r from-emerald-600 to-blue-600 hover:from-emerald-700 hover:to-blue-700 text-white"
          onClick={onSend}
          data-testid="share-bank-send"
        >
          <Send className="w-3 h-3 mr-1" />Send to Bank
        </Button>
      </div>
    </div>
  </div>
);

// ---- Component: Step 3 - PDF preview (full single-page snapshot) ----
const Step3PdfPreview = () => (
  <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200">
    <div className="text-xs text-slate-500 mb-2 uppercase tracking-wide font-semibold flex items-center justify-between">
      <span>Step 3 · The PDF your bank will receive</span>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="h-7 text-xs"><Download className="w-3 h-3 mr-1" />Download</Button>
        <Button size="sm" variant="outline" className="h-7 text-xs"><Eye className="w-3 h-3 mr-1" />Open in new tab</Button>
      </div>
    </div>

    {/* PDF page mockup */}
    <div className="bg-white shadow-2xl mx-auto max-w-[760px] border border-slate-200" style={{ aspectRatio: '8.5 / 11' }}>
      <div className="h-full flex flex-col p-8" style={{ fontFamily: 'Georgia, serif' }}>
        {/* Header band */}
        <div className="border-b-4 border-slate-900 pb-3 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md bg-blue-600 text-white flex items-center justify-center font-bold text-sm">D</div>
                <p className="text-xl font-bold text-slate-900 tracking-tight">DrawReady</p>
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5">Construction Finance Portfolio Snapshot</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Generated</p>
              <p className="text-xs text-slate-900 font-medium">February 7, 2026 · 10:24 AM CST</p>
              <p className="text-[10px] text-slate-500 mt-1">Borrower: <span className="font-bold text-slate-900">Mods Enterprises LLC</span></p>
              <p className="text-[9px] text-slate-400">Authorized signer: Mod Williams</p>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="mb-3">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">For Hills Bank · Loan Officer: Mike Sanders</p>
          <h1 className="text-2xl font-bold text-slate-900 mt-1">Portfolio Snapshot — 5 Active Loans</h1>
        </div>

        {/* Bank-Ready highlight band */}
        <div className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-lg p-3 mb-3">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-white border-4 border-emerald-500 flex items-center justify-center flex-shrink-0">
              <span className="text-xl font-extrabold text-emerald-700">82</span>
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                <Award className="w-3.5 h-3.5 text-emerald-600" />Bank-Ready Score: 82 / 100
              </p>
              <p className="text-[10px] text-slate-600 mt-0.5">
                Composite score across documentation, compliance, and loan transparency.
                Higher score = lower friction at draw time.
              </p>
            </div>
          </div>
          {/* Score breakdown */}
          <div className="bg-white/70 rounded-md p-2 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[9px]">
            {[
              { label: 'Loan principal & terms documented', max: 15, got: 15 },
              { label: 'Legal description on file', max: 10, got: 10 },
              { label: 'Borrower entity verified (W-9 + EIN)', max: 10, got: 10 },
              { label: 'Bank contacts & routing on file', max: 10, got: 10 },
              { label: 'Insurance proof current', max: 10, got: 10 },
              { label: 'Construction phases tracked & up-to-date', max: 10, got: 8 },
              { label: 'Contractor compliance (W-9 / COI / Lien)', max: 15, got: 9 },
              { label: 'Inspections & completion photos', max: 10, got: 5 },
              { label: 'Active draw history & on-time payments', max: 10, got: 5 },
            ].map((row, i) => {
              const pct = row.got / row.max;
              const cls = pct === 1 ? 'text-emerald-700' : pct >= 0.6 ? 'text-amber-700' : 'text-red-700';
              return (
                <div key={i} className="flex items-center justify-between gap-1.5 border-b border-slate-100 last:border-b-0 py-0.5">
                  <div className="flex items-center gap-1 flex-1 min-w-0">
                    {pct === 1 ? <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600 flex-shrink-0" /> :
                      pct >= 0.6 ? <Hourglass className="w-2.5 h-2.5 text-amber-600 flex-shrink-0" /> :
                      <AlertTriangle className="w-2.5 h-2.5 text-red-600 flex-shrink-0" />}
                    <span className="text-slate-700 truncate">{row.label}</span>
                  </div>
                  <span className={`font-mono font-bold ${cls} flex-shrink-0`}>{row.got}/{row.max}</span>
                </div>
              );
            })}
            <div className="col-span-2 flex items-center justify-between pt-1 mt-1 border-t-2 border-slate-300">
              <span className="font-bold text-slate-900 text-[10px]">Total</span>
              <span className="font-mono font-extrabold text-emerald-700 text-[10px]">82 / 100</span>
            </div>
          </div>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
            <p className="text-[9px] text-slate-500 uppercase font-bold">Total Loan Capacity</p>
            <p className="text-sm font-bold text-slate-900 font-mono mt-0.5">$2.6M</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
            <p className="text-[9px] text-slate-500 uppercase font-bold">Drawn</p>
            <p className="text-sm font-bold text-slate-900 font-mono mt-0.5">$1.87M</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
            <p className="text-[9px] text-slate-500 uppercase font-bold">Available</p>
            <p className="text-sm font-bold text-emerald-700 font-mono mt-0.5">$730K</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
            <p className="text-[9px] text-slate-500 uppercase font-bold">MTD Burn</p>
            <p className="text-sm font-bold text-slate-900 font-mono mt-0.5">$4,419</p>
          </div>
        </div>

        {/* Active Loans table */}
        <div className="mb-3">
          <p className="text-[11px] font-bold text-slate-900 mb-1.5 flex items-center gap-1.5">
            <Building2 className="w-3 h-3" />Active Loans
          </p>
          <table className="w-full text-[10px] border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-300 text-slate-600">
                <th className="text-left py-1 font-bold">Project</th>
                <th className="text-right py-1 font-bold">Loan</th>
                <th className="text-right py-1 font-bold">Drawn</th>
                <th className="text-right py-1 font-bold">Available</th>
                <th className="text-center py-1 font-bold">Term</th>
                <th className="text-center py-1 font-bold">Score</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {[
                ['New Build 38th', 2_600_000, 1_870_000, 730_000, '12mo', 82, 'text-emerald-600'],
                ['Solon Modern Farmhouse', 1_400_000, 312_000, 1_088_000, '9mo', 91, 'text-emerald-600'],
                ['Iowa City Renovation', 480_000, 410_000, 70_000, '6mo', 95, 'text-emerald-600'],
                ['Marion Spec Home', 720_000, 110_000, 610_000, '12mo', 65, 'text-amber-600'],
                ['388 Ash Ave', 22_565, 0, 22_565, '24mo', 70, 'text-blue-600'],
              ].map((row, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-1 text-slate-900 font-sans">{row[0]}</td>
                  <td className="text-right py-1">{fmt$(row[1])}</td>
                  <td className="text-right py-1 text-slate-600">{fmt$(row[2])}</td>
                  <td className="text-right py-1 text-emerald-700 font-bold">{fmt$(row[3])}</td>
                  <td className="text-center py-1">{row[4]}</td>
                  <td className={`text-center py-1 font-bold ${row[6]}`}>{row[5]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Compliance summary */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <p className="text-[11px] font-bold text-slate-900 mb-1.5 flex items-center gap-1.5">
              <Shield className="w-3 h-3 text-amber-600" />Contractor Compliance · 12 active
            </p>
            <div className="space-y-1 text-[9px]">
              {[
                { label: 'Fully approved', count: 7, color: 'bg-emerald-100 text-emerald-700' },
                { label: 'Documents pending', count: 3, color: 'bg-amber-100 text-amber-700' },
                { label: 'COI expiring within 30 days', count: 1, color: 'bg-orange-100 text-orange-700' },
                { label: 'New / not yet submitted', count: 1, color: 'bg-red-100 text-red-700' },
              ].map((c, i) => (
                <div key={i} className="flex items-center justify-between bg-slate-50 px-2 py-1 rounded">
                  <span className="text-slate-700">{c.label}</span>
                  <span className={`px-1.5 py-0.5 rounded font-bold ${c.color}`}>{c.count}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[11px] font-bold text-slate-900 mb-1.5 flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3 text-emerald-600" />Documentation Completeness
            </p>
            <div className="space-y-1 text-[9px]">
              {[
                ['Legal descriptions filed', 5, 5],
                ['Bank contacts on file', 5, 5],
                ['W-9s on file', 11, 12],
                ['COIs current', 10, 12],
                ['Lien waivers up to date', 9, 12],
              ].map(([label, a, b], i) => (
                <div key={i} className="flex items-center gap-2 bg-slate-50 px-2 py-1 rounded">
                  <span className="text-slate-700 flex-1">{label}</span>
                  <div className="flex-shrink-0 w-12 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                    <div className="bg-emerald-500 h-1.5" style={{ width: `${(a / b) * 100}%` }} />
                  </div>
                  <span className="text-slate-600 font-mono w-7 text-right">{a}/{b}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer note */}
        <div className="mt-auto pt-3 border-t border-slate-200">
          <div className="flex items-center justify-between text-[8px] text-slate-500">
            <div className="flex items-center gap-1.5">
              <Lock className="w-3 h-3" />
              <span>Confidential — generated by DrawReady · drawready.app</span>
            </div>
            <div>Page 1 of 1 · Verified portfolio data as of 02/07/26 10:24 CST</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ---- Email preview ----
const EmailPreview = () => (
  <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200">
    <div className="text-xs text-slate-500 mb-2 uppercase tracking-wide font-semibold">What your loan officer sees in their inbox</div>
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm max-w-2xl mx-auto overflow-hidden">
      <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 flex items-center gap-2 text-[11px]">
        <Mail className="w-3.5 h-3.5 text-slate-500" />
        <span className="text-slate-600">From: Mod Williams &lt;mod@yourborrower.com&gt;</span>
      </div>
      <div className="px-5 py-4">
        <p className="font-bold text-slate-900 text-base mb-1">Portfolio update — February 2026 (Bank-Ready 82/100)</p>
        <p className="text-[11px] text-slate-500 mb-3">To: Mike Sanders &lt;m.sanders@hillsbank.com&gt; · CC: mod@yourborrower.com</p>
        <p className="text-sm text-slate-700 leading-relaxed mb-3">
          Hi Mike,<br /><br />
          Here&apos;s a portfolio update for our active loans. Bank-Ready score is at 82/100 with full
          compliance on 7 of 12 active contractors.
          The attached PDF includes a full breakdown of available draws, contractor status,
          and documentation completeness. Happy to walk through any questions.
        </p>
        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 mb-2">
          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-2">Attachments</p>
          <div className="flex items-center gap-2 text-xs bg-white rounded-md p-2 border border-slate-200">
            <FileText className="w-4 h-4 text-rose-600" />
            <div className="flex-1">
              <p className="font-medium text-slate-900">DrawReady_Portfolio_Snapshot.pdf</p>
              <p className="text-[10px] text-slate-500">147 KB · 1 page</p>
            </div>
            <Button size="sm" variant="outline" className="h-7 text-xs"><Download className="w-3 h-3 mr-1" />Download</Button>
          </div>
        </div>
        <p className="text-[10px] text-slate-400 mt-3">
          Sent from <span className="font-semibold text-slate-600">DrawReady</span> · Verified borrower data ·
          <a href="#" className="text-blue-600 hover:underline">Reply directly</a>
        </p>
      </div>
    </div>
  </div>
);

// =================== MAIN ===================
export default function ShareWithBankPreview() {
  const [step, setStep] = useState(1);
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-emerald-50 px-6 py-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <Badge className="mb-2 bg-emerald-100 text-emerald-700 hover:bg-emerald-100">DESIGN PREVIEW</Badge>
          <h1 className="text-3xl font-bold text-slate-900 mb-1 flex items-center gap-2">
            <Send className="w-7 h-7 text-emerald-600" />
            Share with Bank — One-click Portfolio Snapshot
          </h1>
          <p className="text-sm text-slate-600 max-w-3xl">
            Click "Share with Bank" on the Command Center. We auto-generate a single-page,
            bank-grade PDF (Portfolio + Bank-Ready + Compliance) and email it to your loan officer.
            They get verified data, you get faster underwriting.
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          {[
            { n: 1, label: 'Trigger from Command Center' },
            { n: 2, label: 'Compose & send email' },
            { n: 3, label: 'See the PDF' },
            { n: 4, label: "Bank's inbox" },
          ].map((s) => (
            <button
              key={s.n}
              onClick={() => setStep(s.n)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                step === s.n ? 'bg-slate-900 text-white shadow-md' : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
              }`}
              data-testid={`share-step-${s.n}`}
            >
              {s.n}. {s.label}
            </button>
          ))}
        </div>

        {step === 1 && <Step1Entry onShare={() => setStep(2)} />}
        {step === 2 && <Step2Compose onSend={() => setStep(3)} onCancel={() => setStep(1)} />}
        {step === 3 && <Step3PdfPreview />}
        {step === 4 && <EmailPreview />}

        {/* Why this matters */}
        <Card className="mt-6 p-5 bg-white border-2 border-emerald-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-600 flex items-center justify-center text-white flex-shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-slate-900 mb-1">Why this is the killer feature</h3>
              <ul className="text-xs text-slate-700 space-y-1">
                <li>· <span className="font-bold">Banks ASK for this.</span> Loan officers spend hours each week chasing borrowers for portfolio status — you give them a verified snapshot in 1 click.</li>
                <li>· <span className="font-bold">Borrowers look professional.</span> Beautiful, branded PDF with verified data instead of a hand-typed email.</li>
                <li>· <span className="font-bold">Viral growth.</span> Every PDF carries the DrawReady logo. Loan officers see it weekly, then ask their other borrowers to use it too.</li>
                <li>· <span className="font-bold">Trust signal.</span> Banks see "Verified portfolio data as of [timestamp]" — different from a borrower-typed report.</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Tech stack note */}
        <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
          <Card className="p-3 bg-white">
            <p className="font-bold text-slate-900 mb-1 flex items-center gap-1"><FileText className="w-3.5 h-3.5 text-rose-600" />PDF generation</p>
            <p className="text-slate-600">Server-side ReportLab — fast, reliable, identical bytes every time. Fallback to existing draw report engine if needed.</p>
          </Card>
          <Card className="p-3 bg-white">
            <p className="font-bold text-slate-900 mb-1 flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-blue-600" />Email delivery</p>
            <p className="text-slate-600">SendGrid (already integrated for Bid Sheets &amp; lien waivers). PDF as attachment. Email tracking optional.</p>
          </Card>
          <Card className="p-3 bg-white">
            <p className="font-bold text-slate-900 mb-1 flex items-center gap-1"><Banknote className="w-3.5 h-3.5 text-emerald-600" />Future expansions</p>
            <p className="text-slate-600">Multi-bank routing, scheduled monthly auto-send, a "Bank can pull this anytime" public read-only link.</p>
          </Card>
        </div>
      </div>
    </div>
  );
}
